import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import useClubPortalBranding from "../../../hooks/useClubPortalBranding";
import { NAV_ICONS, IconSettings, IconBell } from "../../ui/icons";
import GlobalPatientSearch from "../GlobalPatientSearch";
import clinicAPI from "../../../services/clinicApi";
import AdminSplash from "./AdminSplash";
import {
  adminFirstName,
  adminGreeting,
  adminTodayLabel,
} from "./adminAppUtils";
import {
  ADMIN_BOTTOM_TABS,
  buildAdminSidebarNav,
  resolveAdminBottomTab,
  resolveAdminPageTitle,
  resolveAdminTabId,
} from "../../../utils/adminNav";
import { usePwaChrome, ADMIN_PAGE_BG } from "../../../utils/pwaThemeColor";
import {
  usePullToRefresh,
  PullToRefreshIndicator,
} from "../../../hooks/usePullToRefresh";
import { dispatchAdminRefresh } from "../../../hooks/useAdminRefresh";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { usePortalHtmlClass } from "../../../hooks/usePortalHtmlClass";
import { useSkipPwaSplash, useShowPwaBanners } from "../../../hooks/useStandalone";
import {
  isPushSupported,
  registerServiceWorker,
  syncAdminPushSubscriptionIfGranted,
} from "../../../utils/pushNotifications";
import AdminPwaBanners from "./AdminPwaBanners";
import ManageDesktopShell from "../common/ManageDesktopShell";
import SubscriptionGate from "./SubscriptionGate";
import NotificationsDropdown from "../common/NotificationsDropdown";
import s from "./adminApp.module.css";

export default function AdminAppShell({ children }) {
  const { pathname } = useLocation();
  const { tenant, club, profile } = useSelector((state) => state.auth);
  const skipSplash = useSkipPwaSplash();
  const showPwaBanners = useShowPwaBanners();
  const isMobile = useIsMobile(768);
  const [splashDone, setSplashDone] = useState(skipSplash);
  const [chatBadge, setChatBadge] = useState(0);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyBadge, setNotifyBadge] = useState(0);
  const notifyBtnRef = useRef(null);
  const { theme, shellStyle } = useClubPortalBranding(
    "aa",
    null,
    true,
    splashDone,
  );

  usePortalHtmlClass("admin");

  useEffect(() => {
    if (!splashDone || !isPushSupported()) return undefined;
    registerServiceWorker();
    syncAdminPushSubscriptionIfGranted();
    return undefined;
  }, [splashDone]);

  const handleSplashComplete = useCallback(() => setSplashDone(true), []);

  const loadChatBadge = useCallback(async () => {
    try {
      const data = await clinicAPI.getStaffChatInbox();
      const total = (data.threads || []).reduce(
        (sum, t) => sum + (t.unread_count || 0),
        0,
      );
      setChatBadge(total);
    } catch {
      setChatBadge(0);
    }
  }, []);

  const loadNotifyBadge = useCallback(async () => {
    try {
      const data = await clinicAPI.getMyAdminNotifications();
      setNotifyBadge(data.unread_count || 0);
    } catch {
      setNotifyBadge(0);
    }
  }, []);

  useEffect(() => {
    if (!splashDone) return undefined;
    loadChatBadge();
    loadNotifyBadge();
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        loadChatBadge();
        loadNotifyBadge();
      }
    }, 15000);
    return () => window.clearInterval(id);
  }, [splashDone, loadChatBadge, loadNotifyBadge, pathname]);

  useEffect(() => {
    setNotifyOpen(false);
  }, [pathname]);

  const path = pathname.replace(/\/$/, "") || "/manage/app";
  const tab = resolveAdminTabId(pathname);
  const bottomTab = resolveAdminBottomTab(pathname);
  const isMoreHub = path === "/manage/app/more";
  const isOverview = tab === "overview";
  const isSchedule = tab === "schedule";
  const isJournal = tab === "journal";
  const hubTabs = new Set([
    "journal",
    "life",
    "notifications",
    "groups",
    "memberships",
    "finance",
    "venues",
    "team",
    "trainers",
    "settings",
    "analytics",
    "directions",
    "profile",
    "polls",
  ]);
  const hideTopBar =
    hubTabs.has(tab) || tab === "clients" || tab === "chat" || tab === "schedule";
  const selfHeader = isOverview || isMoreHub || tab === "life";
  const pageTitle = resolveAdminPageTitle(tab, club, tenant);
  const firstName = adminFirstName(profile?.full_name);

  usePwaChrome(isOverview ? theme?.from : ADMIN_PAGE_BG, {
    enabled: splashDone && Boolean(theme) && isMobile,
    isDark: isOverview,
    immersive: isOverview,
  });

  const handlePullRefresh = useCallback(async () => {
    dispatchAdminRefresh();
    await new Promise((r) => setTimeout(r, 400));
  }, []);

  const noPtrTabs = new Set(["settings", "profile"]);
  const { pulling, pullDistance, threshold } = usePullToRefresh(handlePullRefresh, {
    enabled: splashDone && isMobile && !noPtrTabs.has(tab),
  });

  const useShellScroll = !isSchedule;
  const adminNav = buildAdminSidebarNav(club, tenant);
  const desktopSections = [
    { items: adminNav.main },
    ...(adminNav.more?.length
      ? [{ label: "Разделы", items: adminNav.more }]
      : []),
  ];
  const flushDesktop =
    hubTabs.has(tab) || tab === "clients" || tab === "chat" || tab === "schedule";

  if (!isMobile && splashDone) {
    return (
      <>
        <ManageDesktopShell
          homeTo="/manage/app"
          sections={desktopSections}
          chatBadge={chatBadge}
          notifyBadge={notifyBadge}
          notifyOpen={notifyOpen}
          onNotifyToggle={() => setNotifyOpen((v) => !v)}
          notifyBtnRef={notifyBtnRef}
          settingsTo="/manage/app/settings?tab=portal"
          profileTo="/manage/app/profile"
          pageTitle={!isOverview && !isJournal ? pageTitle : null}
          isOverview={isOverview}
          isSchedule={isSchedule}
          isJournal={isJournal}
          flushContent={flushDesktop}
          shellStyle={shellStyle}
        >
          <SubscriptionGate>
            <div key={pathname} className={s.pageEnter}>
              {children}
            </div>
          </SubscriptionGate>
        </ManageDesktopShell>
        <NotificationsDropdown
          open={notifyOpen}
          onClose={() => setNotifyOpen(false)}
          anchorRef={notifyBtnRef}
          allPath="/manage/app/notifications"
          portal="admin"
          onMarkedRead={loadNotifyBadge}
        />
      </>
    );
  }

  return (
    <>
      {!splashDone ? (
        <AdminSplash
          clubName={tenant?.name}
          onComplete={handleSplashComplete}
        />
      ) : null}

      <div className={s.shell} style={splashDone ? shellStyle : undefined}>
        {useShellScroll ? (
          <div className={s.shellScroll} data-pull-scroll>
            {isOverview ? (
              <div className={`${s.heroHome} ${splashDone ? "" : s.heroHidden}`}>
                <div
                  className={`${s.hero} ${
                    theme.coverUrl ? s.heroCover : s.heroGradient
                  }`}
                >
                  <div
                    className={s.heroBg}
                    key={theme.coverUrl || "solid"}
                    style={
                      theme.coverUrl
                        ? { backgroundImage: `url(${theme.coverUrl})` }
                        : { backgroundColor: theme.accent }
                    }
                    aria-hidden
                  />
                  <div className={s.heroTop}>
                    <div className={s.heroMeta}>
                      <p className={s.heroDate}>{adminTodayLabel()}</p>
                      <h1 className={s.heroTitle}>
                        {adminGreeting()}
                        {firstName ? `, ${firstName}` : ""}
                      </h1>
                      {tenant?.name ? (
                        <p className={s.heroClub}>{tenant.name}</p>
                      ) : null}
                    </div>
                    <div className={s.heroTopActions}>
                      <button
                        ref={notifyBtnRef}
                        type="button"
                        className={s.heroNotify}
                        title="Уведомления"
                        aria-label="Уведомления"
                        aria-expanded={notifyOpen}
                        onClick={() => setNotifyOpen((v) => !v)}
                      >
                        <IconBell width={20} height={20} />
                        {notifyBadge > 0 ? (
                          <span className={s.tabBadge}>{notifyBadge > 9 ? "9+" : notifyBadge}</span>
                        ) : null}
                      </button>
                      <Link
                        to="/manage/app/settings?tab=portal"
                        className={s.heroSettings}
                        title="Персонализация"
                        aria-label="Персонализация"
                      >
                        <IconSettings width={20} height={20} />
                      </Link>
                    </div>
                  </div>
                </div>
                <div className={s.searchFloat}>
                  <GlobalPatientSearch className={s.searchFullWidth} />
                </div>
              </div>
            ) : null}

            {!isOverview && !isMoreHub && !hideTopBar ? (
              <div className={`${s.topBar} ${splashDone ? "" : s.tabBarHidden}`}>
                {pageTitle ? (
                  <header className={s.pageHead}>
                    <h1 className={s.pageTitle}>{pageTitle}</h1>
                  </header>
                ) : null}
              </div>
            ) : null}

            <main
              className={
                selfHeader ? s.mainHome : s.main
              }
            >
              <SubscriptionGate>
                <div key={pathname} className={s.pageEnter}>
                  {children}
                </div>
              </SubscriptionGate>
            </main>
          </div>
        ) : (
          <>
            {!isOverview && !isMoreHub && !hideTopBar ? (
              <div className={`${s.topBar} ${splashDone ? "" : s.tabBarHidden}`}>
                {pageTitle ? (
                  <header className={s.pageHead}>
                    <h1 className={s.pageTitle}>{pageTitle}</h1>
                  </header>
                ) : null}
              </div>
            ) : null}

            <main className={s.mainSchedule}>
              <SubscriptionGate>
                <div key={pathname} className={s.pageEnter}>
                  {children}
                </div>
              </SubscriptionGate>
            </main>
          </>
        )}

        <PullToRefreshIndicator
          pulling={pulling}
          pullDistance={pullDistance}
          threshold={threshold}
        />

        {splashDone && showPwaBanners ? <AdminPwaBanners /> : null}

        <nav
          className={`${s.tabBar} ${splashDone ? s.tabBarReveal : s.tabBarHidden}`}
          aria-label="Навигация"
        >
          {ADMIN_BOTTOM_TABS.map((item) => {
            const Icon = NAV_ICONS[item.icon] || NAV_ICONS.overview;
            const active = item.id === bottomTab;
            const badge = item.id === "chat" && chatBadge > 0 ? chatBadge : 0;
            return (
              <Link
                key={item.id}
                to={item.to}
                end={item.end}
                className={active ? `${s.tab} ${s.tabActive}` : s.tab}
                aria-current={active ? "page" : undefined}
              >
                <span className={s.tabIconWrap}>
                  <Icon className={s.tabIcon} />
                  {badge > 0 ? (
                    <span className={s.tabBadge}>{badge > 9 ? "9+" : badge}</span>
                  ) : null}
                </span>
                <span className={s.tabLabel}>{item.label}</span>
              </Link>
            );
          })}
        </nav>
      </div>

      <NotificationsDropdown
        open={notifyOpen}
        onClose={() => setNotifyOpen(false)}
        anchorRef={notifyBtnRef}
        allPath="/manage/app/notifications"
        portal="admin"
        onMarkedRead={loadNotifyBadge}
      />
    </>
  );
}
